<template>
  <box>
    <template #header>
      <h2 class="header-title">{{ t('Etapas de Produção') }}</h2>
    </template>

    <div v-if="steps.length === 0" class="empty-state">
      {{ t('Nenhuma etapa de produção encontrada') }}
    </div>

    <div v-else class="steps-container">
      <div
        v-for="(step, index) in steps"
        :key="step.so_work_order_step_id"
        class="step-row"
      >
        <!-- Step number indicator -->
        <div
          class="step-number"
          :class="stepStatusClass(step.so_work_order_step_status)"
        >
          {{ index + 1 }}
        </div>

        <!-- Step details -->
        <div class="step-details">
          <div class="step-header">
            <span class="step-name">{{ step.so_work_order_step_name }}</span>
            <Tag
              :value="statusLabel(step.so_work_order_step_status)"
              :severity="statusSeverity(step.so_work_order_step_status)"
              size="small"
            />
          </div>

          <div v-if="step.so_work_order_step_notes" class="step-notes">
            {{ step.so_work_order_step_notes }}
          </div>

          <div class="step-timestamps">
            <span v-if="step.so_work_order_step_started_at" class="timestamp">
              {{ t('Inicio') }}: {{ formatDateTime(new Date(step.so_work_order_step_started_at)) }}
            </span>
            <span v-if="step.so_work_order_step_completed_at" class="timestamp">
              {{ t('Fim') }}: {{ formatDateTime(new Date(step.so_work_order_step_completed_at)) }}
            </span>
          </div>
        </div>
      </div>
    </div>
  </box>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useDateFormatter } from '../../composables/useDateFormatter';
import type { Orders_WorkOrderStepRow } from '../../contracts/Orders_WorkOrderStepRow';

const { t } = useI18n();
const { formatDateTime } = useDateFormatter();

interface Props {
  steps?: Orders_WorkOrderStepRow[];
}

const props = withDefaults(defineProps<Props>(), {
  steps: () => [],
});

function stepStatusClass(status: string): string {
  switch (status) {
    case 'completed':
      return 'step-status-completed';
    case 'in_progress':
      return 'step-status-in-progress';
    case 'skipped':
      return 'step-status-skipped';
    default:
      return 'step-status-pending';
  }
}

function statusLabel(status: string): string {
  switch (status) {
    case 'completed':
      return t('Concluído');
    case 'in_progress':
      return t('Em andamento');
    case 'skipped':
      return t('Pulado');
    default:
      return t('Pendente');
  }
}

function statusSeverity(status: string): string {
  switch (status) {
    case 'completed':
      return 'success';
    case 'in_progress':
      return 'info';
    case 'skipped':
      return 'warn';
    default:
      return 'contrast';
  }
}
</script>

<style scoped>
.header-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--p-text-color);
}

.empty-state {
  font-size: 0.875rem;
  color: var(--p-text-muted-color);
  padding: 0.5rem 0;
}

.steps-container {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.step-row {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid var(--p-surface-border);
}

.step-row:last-child {
  border-bottom: none;
}

.step-number {
  flex-shrink: 0;
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.875rem;
  font-weight: 500;
}

.step-status-completed {
  background-color: var(--p-green-100);
  color: var(--p-green-700);
}

.step-status-in-progress {
  background-color: var(--p-blue-100);
  color: var(--p-blue-700);
}

.step-status-skipped {
  background-color: var(--p-surface-200);
  color: var(--p-text-muted-color);
}

.step-status-pending {
  background-color: var(--p-surface-200);
  color: var(--p-text-color);
}

.step-details {
  flex: 1;
  min-width: 0;
}

.step-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.step-name {
  font-weight: 500;
  font-size: 0.875rem;
  color: var(--p-text-color);
}

.step-notes {
  font-size: 0.75rem;
  color: var(--p-text-muted-color);
  margin-top: 0.25rem;
}

.step-timestamps {
  display: flex;
  gap: 1rem;
  margin-top: 0.25rem;
  font-size: 0.75rem;
  color: var(--p-text-muted-color);
}

.timestamp {
  display: block;
}
</style>
